import {
  BsGraphUp,
  BsDownload,
  BsEye,
  BsFillBarChartFill,
} from "react-icons/bs";
import { Button } from "../../components/Form";
import { useEffect, useState, useRef } from "react";
import Modal from "../../components/Modals/Modal";
import { ChartPediatrics } from "../../components/Charts";
import { Pediatrics } from "../../components/Datas";
import { pdf } from "@react-pdf/renderer";
import ChartPdf from "../../pdf/ChartPdf";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";

const getNumericValue = (value, multiplier = 1) => {
  const num = parseFloat(value);
  return isNaN(num) ? null : num * multiplier;
};

const BASE_CHART_OPTIONS = {
  names: [],
  options: {
    chart: {
      height: 400,
      type: "line",
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    colors: ["#fad7a0", "#f9e79f", "#abebc6", "#aed6f1", "#e6b0aa", "#000"],
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: [3, 3, 3, 3, 3, 3],
      dashArray: [0, 0, 0, 0, 0, 0],
    },
    title: { text: "Gráfica de Crecimiento", align: "left" },
    grid: {
      borderColor: "#e7e7e7",
      row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 },
    },
    markers: {
      size: [0, 0, 0, 0, 0, 0],
      colors: ["#fad7a0", "#f9e79f", "#abebc6", "#aed6f1", "#e6b0aa", "#000"],
    },
    xaxis: { title: { text: "Eje X" }, type: "numeric" },
    yaxis: { title: { text: "Eje Y" } },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: false,
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const x = w.globals.seriesX[seriesIndex][dataPointIndex];
        const y = series[seriesIndex][dataPointIndex];
        return `
          <div style="padding: 5px 10px;">
            <div><strong>X:</strong> ${x}</div>
            <div><strong>Y:</strong> ${y}</div>
          </div>`;
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      floating: false,
      offsetY: -25,
      offsetX: -5,
    },
  },
};

const StadisticsResults = ({ options_datas }) => {
  const [openModal, setOpenModal] = useState(false);
  const [chartType, setChartType] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [dataShow, setDataShow] = useState(null);
  const [chartOptions, setChartOptions] = useState(BASE_CHART_OPTIONS);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const chartRef = useRef(null);

  const getScatterData = (
    citas = [],
    xField,
    yField,
    xMultiplier = 1,
    yMultiplier = 1
  ) => {
    return citas
      .map((cita) => {
        // Validación más estricta
        if (!cita || cita[xField] === undefined || cita[yField] === undefined) {
          return null;
        }

        // Normalización de valores
        const xVal = parseFloat(cita[xField]) * xMultiplier;
        const yVal = parseFloat(cita[yField]) * yMultiplier;

        // Validación de rangos
        if (isNaN(xVal) || isNaN(yVal) || xVal < 0 || yVal < 0) {
          return null;
        }

        return [xVal, yVal];
      })
      .filter((point) => point !== null)
      .sort((a, b) => a[0] - b[0]); // Ordenar por eje X
  };

  const handleOpenChart = (type) => {
    setChartType(type);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setChartType(null);
    setModalTitle("");
  };

  useEffect(() => {
    if (!chartType || !options_datas?.citas) return;

    const gender = options_datas.gender;
    const name = options_datas.name;
    const newOptions = JSON.parse(JSON.stringify(BASE_CHART_OPTIONS));

    let referenceData = [];
    let patientPoints = [];

    switch (chartType) {
      case "HeightWeight":
        setModalTitle("IMC");
        newOptions.names = [
          "Sobrepeso",
          "Peso Alto",
          "Ideal",
          "Bajo peso",
          "Desnutrición",
          "Paciente",
        ];
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.options.yaxis.title.text = "IMC";
        referenceData =
          gender === "Masculino"
            ? Pediatrics.WeightLengthBoys02
            : Pediatrics.WeightLengthGirls02;

        patientPoints = getScatterData(options_datas.citas, "age", "bmi");

        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 228;
        newOptions.options.yaxis.min = 0;
        newOptions.options.yaxis.max = 40;
        break;

      case "WeightAge":
        setModalTitle("Peso/Edad");
        referenceData =
          gender === "Masculino"
            ? Pediatrics.WeightMountBoys
            : Pediatrics.WeightMountGirls;

        patientPoints = getScatterData(
          options_datas.citas,
          "age",
          "peso",
          1,
          1
        );
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.options.yaxis.title.text = "Peso (kg)";
        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 228;
        newOptions.options.yaxis.min = 0;
        newOptions.options.yaxis.max = 110;
        break;

      case "HeightAge":
        setModalTitle("Altura/Edad");
        referenceData =
          gender === "Masculino"
            ? Pediatrics.LengthMountBoys
            : Pediatrics.LengthMountGirls;

        patientPoints = getScatterData(
          options_datas.citas,
          "age",
          "altura",
          1,
          1
        );
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.options.yaxis.title.text = "Altura (cm)";
        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 228;
        newOptions.options.yaxis.min = 40;
        newOptions.options.yaxis.max = 200;
        break;

      case "HeadCircunference":
        setModalTitle("Circunferencia de cabeza/Edad");
        referenceData =
          gender === "Masculino"
            ? Pediatrics.HeadCircunferenceBoys
            : Pediatrics.HeadCircunferenceGirls;

        patientPoints = getScatterData(
          options_datas.citas,
          "age",
          "circunferencia"
        );
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.options.yaxis.title.text = "Cabeza (cm)";
        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 60;
        newOptions.options.yaxis.min = 25;
        newOptions.options.yaxis.max = 55;
        break;

      default:
        return;
    }

    const toXY = (arr = []) => arr.map((y, i) => [i, y]);

    const completeDataShow = {
      sd3: toXY(referenceData?.sd3),
      sd2: toXY(referenceData?.sd2),
      median: toXY(referenceData?.median),
      sdneg2: toXY(referenceData?.sdneg2),
      sdneg3: toXY(referenceData?.sdneg3),
      patient: patientPoints,
    };

    setDataShow(completeDataShow);
    setChartOptions(newOptions);
  }, [chartType, options_datas]);

  const handleDownloadPDF = async () => {
    if (!chartRef.current) return;

    setIsGeneratingPdf(true);
    try {
      // 1. Generar imagen del gráfico
      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const imageData = canvas.toDataURL("image/png");

      // 2. Crear el PDF con la imagen generada
      const pdfDoc = (
        <ChartPdf
          chartTitle={modalTitle}
          patientData={options_datas}
          chartImage={imageData}
        />
      );

      // 3. Forzar la descarga del PDF
      const blob = await pdf(pdfDoc).toBlob();
      saveAs(blob, `Grafico_${modalTitle}.pdf`);
    } catch (error) {
      console.error("Error al generar el PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5">
      {openModal && (
        <Modal
          closeModal={handleCloseModal}
          isOpen={openModal}
          title={`Comparación de ${modalTitle}`}
          size="xl"
        >
          <div ref={chartRef}>
            {dataShow && chartOptions && (
              <ChartPediatrics datas={dataShow} options={chartOptions} />
            )}
          </div>

          <div className="flex justify-end mt-4">
            {isGeneratingPdf ? (
              <Button disabled>
                <span className="animate-pulse">Generando PDF...</span>
              </Button>
            ) : (
              <Button
                label="Descargar PDF del grafico"
                Icon={BsDownload}
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white"
              >
                Descargar PDF
              </Button>
            )}
          </div>
        </Modal>
      )}

      {[
        { label: "IMC", type: "HeightWeight" },
        { label: "Peso/Edad", type: "WeightAge" },
        { label: "Altura/Edad", type: "HeightAge" },
        { label: "Circunferencia de cabeza/Edad", type: "HeadCircunference" },
      ].map(({ label, type }) => (
        <div
          key={type}
          className="grid grid-cols-6 items-center text-sm leading-none mb-1"
        >
          <div className="col-span-5">
            <p className="text-xs font-medium">{label}</p>
          </div>
          <div className="col-span-1 flex justify-end">
            <button
              onClick={() => handleOpenChart(type)}
              className=" hover:bg-yellow-400 hover:text-white text-yellow-400  p-[2px] rounded-md mr-2 text-md"
              style={{ width: "35px", height: "22px" }} // Ajusta tamaño del botón
            >
              <BsFillBarChartFill size={15} /> {/* Tamaño de ícono */}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StadisticsResults;
